import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, type TripLifecycleEvent, type Complaint } from '@/lib/adminApi';
import { Card, Spinner, EmptyState, Badge, type BadgeTone } from '@/components/ui';
import { Drawer, Field } from '@/components/admin/Drawer';
import { dateTime } from '@/lib/adminFormat';
import { useAdminPermission } from '@/lib/useAdminPermission';
import { ApiError } from '@/lib/api';

type Tab = 'detail' | 'lifecycle' | 'complaints';

// Trip status → tone (mirrors the Trips list page's STATUS map; the label comes
// from the server's status_name so the two never drift).
function statusTone(status: number): BadgeTone {
  switch (status) {
    case 1:
    case 2:
      return 'info';
    case 3:
      return 'warn';
    case 5:
      return 'ok';
    case 6:
    case 7:
      return 'danger';
    default:
      return 'neutral';
  }
}

// Lifecycle event → tone for the timeline dot.
function eventTone(eventType: string): BadgeTone {
  switch (eventType) {
    case 'trip.requested':
    case 'trip.accepted':
    case 'trip.driver_arrived':
      return 'info';
    case 'trip.started':
    case 'trip.plan':
    case 'trip.plan_published':
      return 'warn';
    case 'trip.completed':
      return 'ok';
    case 'trip.cancelled':
      return 'danger';
    default:
      return 'neutral';
  }
}

function participantName(p?: { first_name: string; last_name: string; phone: string; id: number } | null): string {
  if (!p) return '';
  const n = `${p.first_name} ${p.last_name}`.trim();
  return n || p.phone || `#${p.id}`;
}

/** TripDrawer — full detail for one trip: the ride (schedule, geo, money, PIN),
 *  the lifecycle timeline (from trip_outbox), the participants (rider + driver),
 *  any complaints, and a gated force-cancel control. DEFENSIVE end to end: when
 *  the trips table isn't in Postgres yet the drawer shows a clean "not yet
 *  available" state driven by the API's available flag. Covers the Filament
 *  TripResource / TripRequestResource view. */
export function TripDrawer({ id, onClose }: { id: number; onClose: () => void }) {
  const [tab, setTab] = useState<Tab>('detail');
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'trip', id],
    queryFn: ({ signal }) => adminApi.tripDetail(id, signal),
  });

  const t = data?.trip;
  const unavailable = data && data.available === false;
  const lifecycleCount = data?.lifecycle?.items?.length ?? 0;
  const complaintsCount = data?.complaints?.items?.length ?? 0;

  return (
    <Drawer
      title={t ? `Trip ${t.trip_id || `#${t.id}`}` : `Trip #${id}`}
      subtitle={data?.trip_type_name && data?.status_name ? `${data.trip_type_name} · ${data.status_name}` : undefined}
      onClose={onClose}
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner label="Loading trip…" />
        </div>
      ) : isError ? (
        <EmptyState title="Couldn't load trip" description={(error as Error)?.message} />
      ) : unavailable ? (
        <EmptyState
          title="Trips not yet in Postgres"
          description={
            data?.note ??
            'The trips table is owned by trip-svc and is still in Laravel/MySQL pre-cutover. This drawer lights up automatically once the table lands here.'
          }
        />
      ) : t ? (
        <div className="space-y-4">
          <Tabs tab={tab} onTab={setTab} lifecycleCount={lifecycleCount} complaintsCount={complaintsCount} />

          {tab === 'detail' ? (
            <div className="space-y-4">
              {/* Status + identity */}
              <div>
                <Field label="Trip ref" value={t.trip_id || '—'} />
                <Field label="Internal ID" value={`#${t.id}`} />
                <Field label="Status" value={<Badge tone={statusTone(t.status)}>{data?.status_name ?? `#${t.status}`}</Badge>} />
                <Field label="Product" value={data?.trip_type_name ?? `Type ${t.trip_type}`} />
                <Field label="Booked" value={dateTime(t.created_at)} />
              </div>

              {/* Participants */}
              <Card>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Participants</div>
                <Field
                  label="Rider"
                  value={
                    data?.customer
                      ? `${participantName(data.customer)} (#${t.customer_id})`
                      : `#${t.customer_id}`
                  }
                />
                <Field
                  label="Driver"
                  value={
                    t.driver_id == null
                      ? <span className="text-muted">unassigned</span>
                      : data?.driver
                        ? `${participantName(data.driver)} (#${t.driver_id})`
                        : `#${t.driver_id}`
                  }
                />
              </Card>

              {/* Route */}
              <Card>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Route</div>
                <Field label="Pickup" value={t.pickup_address || '—'} />
                <Field label="Drop" value={t.drop_address || '—'} />
                <Field label="Distance" value={t.distance_km ? `${t.distance_km.toFixed(2)} km` : '—'} />
                {t.otp ? <Field label="Pickup PIN" value={<span className="metric">{t.otp}</span>} /> : null}
              </Card>

              {/* Delivery (Logistics/Retail) — recipient + the verification-chain
                  evidence: sealed-package photo (pre-pickup), drop-off PIN
                  (verified on completion), signature + optional delivered photo. */}
              {(t.trip_type === 4 || t.trip_type === 13) ? (
                <Card>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Delivery</div>
                  <Field label="Content" value={t.delivery_content || '—'} />
                  <Field label="Recipient phone" value={t.recipient_phone || '—'} />
                  <Field label="Received by" value={t.recipient_name || '—'} />
                  {t.drop_otp ? (
                    <Field
                      label="Drop-off PIN"
                      value={
                        <span className="flex items-center gap-2">
                          <span className="metric">{t.drop_otp}</span>
                          {t.status === 5 ? <Badge tone="ok">verified at hand-over</Badge> : null}
                        </span>
                      }
                    />
                  ) : (
                    <Field label="Drop-off PIN" value={<span className="text-muted">not on this trip (pre-verification booking)</span>} />
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <MediaButton tripId={t.id} which="sealed" label="Sealed-package photo" present={!!t.sealed_package_url} />
                    <MediaButton tripId={t.id} which="delivered" label="Delivered photo" present={!!t.delivered_photo_url} />
                    <MediaButton tripId={t.id} which="signature" label="Recipient signature" present={!!t.signature_url} />
                  </div>
                </Card>
              ) : null}

              {/* Money breakdown */}
              <Card>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Fare</div>
                <Field label="Total" value={money(t.total)} />
                <Field label="Estimated" value={money(t.estimated_fare)} />
                <Field label="Subtotal" value={money(t.sub_total)} />
                <Field label="Tax" value={money(t.tax)} />
                {t.discount ? <Field label="Discount" value={money(t.discount)} /> : null}
                {t.tip ? <Field label="Tip" value={money(t.tip)} /> : null}
                {t.on_time_bonus ? (
                  <Field
                    label="On-time bonus"
                    value={<span>{money(t.on_time_bonus)} <span className="text-muted">→ driver ({t.retail_window || 'window'} met)</span></span>}
                  />
                ) : null}
                {t.no_show ? (
                  <Field
                    label="No-show fee"
                    value={
                      <span className="flex items-center gap-2">
                        {money(t.no_show_fee)}
                        <Badge tone="warn">customer no-show → paid to driver</Badge>
                      </span>
                    }
                  />
                ) : null}
                {t.cancel_fee ? (
                  <Field
                    label="Cancellation fee"
                    value={
                      <span className="flex items-center gap-2">
                        {money(t.cancel_fee)}
                        <Badge tone="warn">late customer cancel → paid to driver</Badge>
                      </span>
                    }
                  />
                ) : null}
                <Field label="Payment" value={t.payment_method || '—'} />
              </Card>

              {/* Schedule (reserve etc.) */}
              {t.pickup_date || t.start_time || t.end_time ? (
                <Card>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Schedule</div>
                  {t.pickup_date ? <Field label="Pickup date" value={dateTime(t.pickup_date)} /> : null}
                  {t.start_time ? <Field label="Started" value={dateTime(t.start_time)} /> : null}
                  {t.end_time ? <Field label="Ended" value={dateTime(t.end_time)} /> : null}
                </Card>
              ) : null}

              {/* Force-cancel control (gated + audited; currently 501 until
                  trip-svc grows an internal admin-cancel endpoint). */}
              <CancelControl tripId={t.id} cancellable={data?.cancellable ?? false} />
            </div>
          ) : tab === 'lifecycle' ? (
            <Lifecycle
              available={data?.lifecycle?.available ?? false}
              note={data?.lifecycle?.note}
              items={data?.lifecycle?.items ?? []}
            />
          ) : (
            <Complaints
              available={data?.complaints?.available ?? false}
              note={data?.complaints?.note}
              items={data?.complaints?.items ?? []}
            />
          )}
        </div>
      ) : (
        <EmptyState title="Trip not found" />
      )}
    </Drawer>
  );
}

/** The lifecycle timeline — one toned dot per trip_outbox event, oldest→newest. */
/** One delivery-evidence viewer button. The bucket is private, so a click
 *  mints a short-lived presigned GET via admin-svc (audited) and opens it in a
 *  new tab. Renders disabled ("not captured") when the trip has no such media. */
function MediaButton({
  tripId,
  which,
  label,
  present,
}: {
  tripId: number;
  which: 'sealed' | 'delivered' | 'signature';
  label: string;
  present: boolean;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const open = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await adminApi.signTripMedia(tripId, which);
      window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };
  if (!present) {
    return (
      <span className="inline-flex cursor-not-allowed items-center rounded-md border border-ink-800/60 px-2.5 py-1.5 text-xs text-muted">
        {label} — not captured
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={open}
      disabled={loading}
      className="btn-secondary inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs"
      title="Opens a short-lived signed link in a new tab (view is audited)"
    >
      {loading ? 'Opening…' : error ? `${label} — retry` : label}
    </button>
  );
}

function Lifecycle({ available, note, items }: { available: boolean; note?: string; items: TripLifecycleEvent[] }) {
  if (!available) {
    return (
      <EmptyState
        title="Timeline not yet available"
        description={note ?? 'The trip_outbox table is owned by trip-svc and is not in this Postgres yet. The lifecycle lights up once it lands.'}
      />
    );
  }
  if (items.length === 0) {
    return <EmptyState title="No lifecycle events" description="This trip has no recorded transitions yet." />;
  }
  return (
    <div className="space-y-1">
      {items.map((e, i) => (
        <div key={`${e.event_type}-${i}`} className="flex items-start gap-3 border-b border-ink-800/60 py-2">
          <Badge tone={eventTone(e.event_type)} dot>
            {e.label}
          </Badge>
          <div className="ml-auto text-right">
            <div className="text-xs text-slate-300">{dateTime(e.occurred_at)}</div>
            <div className="text-[11px] text-muted">
              {e.status_name ? `→ ${e.status_name}` : e.event_type}
              {e.published ? '' : ' · queued'}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Complaints filed against this trip (read-only). */
function Complaints({ available, note, items }: { available: boolean; note?: string; items: Complaint[] }) {
  if (!available) {
    return (
      <EmptyState
        title="Complaints not yet available"
        description={note ?? 'The complaints table is owned by comms-svc and is not in this Postgres yet.'}
      />
    );
  }
  if (items.length === 0) {
    return <EmptyState title="No complaints" description="No complaints have been filed against this trip." />;
  }
  return (
    <div className="space-y-2">
      {items.map((c) => (
        <Card key={c.id}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-slate-100">{c.subject || `Complaint #${c.id}`}</div>
              {c.description ? <p className="mt-1 text-xs text-muted">{c.description}</p> : null}
            </div>
            <span className="shrink-0 text-[11px] text-muted">{dateTime(c.created_at)}</span>
          </div>
        </Card>
      ))}
    </div>
  );
}

/** Force-cancel control. Only ops + superadmin hold trips.cancel; others don't
 *  see the button (admin-svc enforces this too). The write is currently 501
 *  ("not yet wired") — the mutation surfaces that reason cleanly instead of a
 *  generic error, so a staffer understands it's pending the trip-svc endpoint. */
function CancelControl({ tripId, cancellable }: { tripId: number; cancellable: boolean }) {
  const canCancel = useAdminPermission('trips.cancel');
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () => adminApi.cancelTrip(tripId, reason),
    onSuccess: () => {
      setConfirming(false);
      qc.invalidateQueries({ queryKey: ['admin', 'trip', tripId] });
    },
  });

  if (!canCancel) return null;

  // The not-yet-wired 501 surfaces as ApiError(501, reason) — show the precise
  // reason so the control reads as "pending the trip-svc endpoint", not "broke".
  const notWired = mutation.isError && mutation.error instanceof ApiError && mutation.error.status === 501;

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-100">Force-cancel trip</div>
          <p className="mt-1 text-xs text-muted">
            Cancels the ride, releases the driver, and refunds any fare via trip-svc. Audited.
          </p>
        </div>
        <button
          type="button"
          disabled={!cancellable || mutation.isPending}
          onClick={() => {
            setConfirming((v) => !v);
            mutation.reset();
          }}
          className="shrink-0 rounded-md bg-danger/15 px-2.5 py-1 text-xs font-medium text-danger ring-1 ring-inset ring-danger/30 hover:bg-danger/25 disabled:opacity-40"
          title={cancellable ? undefined : 'This trip is already in a terminal state.'}
        >
          Cancel trip
        </button>
      </div>

      {confirming ? (
        <div className="mt-2 flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <label className="block text-[11px] text-muted">Reason (recorded in the audit trail)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. fraudulent booking / rider request"
              className="mt-1 input"
            />
          </div>
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
            className="rounded-md bg-danger px-3 py-1.5 text-xs font-semibold text-white hover:bg-danger/90 disabled:opacity-50"
          >
            {mutation.isPending ? 'Cancelling…' : 'Confirm cancel'}
          </button>
        </div>
      ) : null}

      {mutation.isSuccess ? <p className="mt-2 text-xs text-ok">{mutation.data?.message}</p> : null}
      {notWired ? (
        <p className="mt-2 rounded-md border border-warn/30 bg-warn/10 px-2.5 py-1.5 text-xs text-warn">
          Not yet wired: {(mutation.error as ApiError).message}
        </p>
      ) : mutation.isError ? (
        <p className="mt-2 text-xs text-danger">{(mutation.error as Error)?.message}</p>
      ) : null}
    </Card>
  );
}

function Tabs({
  tab,
  onTab,
  lifecycleCount,
  complaintsCount,
}: {
  tab: Tab;
  onTab: (t: Tab) => void;
  lifecycleCount: number;
  complaintsCount: number;
}) {
  const item = (key: Tab, label: string) => (
    <button
      type="button"
      onClick={() => onTab(key)}
      className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
        tab === key ? 'bg-brand-600/15 text-brand-400' : 'text-muted hover:bg-ink-800 hover:text-slate-200'
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex gap-1 rounded-lg border border-ink-800 bg-ink-850 p-1">
      {item('detail', 'Detail')}
      {item('lifecycle', `Lifecycle${lifecycleCount ? ` (${lifecycleCount})` : ''}`)}
      {item('complaints', `Complaints${complaintsCount ? ` (${complaintsCount})` : ''}`)}
    </div>
  );
}

/** A compact dollar formatter that shows "—" for a zero/absent amount. */
function money(n: number | null | undefined): string {
  if (!n) return '—';
  return `$${n.toFixed(2)}`;
}
